import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * VideoLightbox — opens a Streamable video centred over the deck in
 * portrait mode (9:16). Click the backdrop, the close button, or press
 * Escape to dismiss.
 *
 * Usage:
 *   <VideoLightbox open={open} onClose={...} streamableId="c0f05z" />
 *
 * The Streamable iframe handles auto-play (muted), playback controls and
 * signed CDN URLs for us. We never touch the raw mp4.
 */
export default function VideoLightbox({ open, onClose, streamableId, label }) {
  // Close on Esc
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  // Lock background scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[300] flex items-center justify-center"
          style={{
            background: "rgba(0,0,0,0.92)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          }}
          onClick={onClose}
          data-testid="video-lightbox"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            className="absolute top-6 right-6 w-12 h-12 rounded-full flex items-center justify-center transition-colors"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "rgba(255,255,255,0.85)",
            }}
            aria-label="Close video"
            data-testid="video-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Optional caption */}
          {label && (
            <div
              className="absolute top-6 left-6 text-[11px] tracking-[0.32em] uppercase"
              style={{ color: "rgba(255,255,255,0.65)", fontWeight: 700 }}
            >
              {label}
            </div>
          )}

          {/* Portrait video frame */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative rounded-2xl overflow-hidden"
            style={{
              aspectRatio: "9 / 16",
              height: "min(92vh, 920px)",
              background: "#000",
              boxShadow: "0 40px 100px -20px rgba(0,0,0,0.85), 0 0 60px rgba(150,193,31,0.18)",
            }}
            data-testid="video-lightbox-frame"
          >
            <iframe
              src={`https://streamable.com/e/${streamableId}?autoplay=1&controls=1`}
              allow="autoplay; fullscreen"
              allowFullScreen
              frameBorder="0"
              className="absolute inset-0 w-full h-full"
              title={label || "Video"}
            />
          </motion.div>

          {/* Hint */}
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.32em] uppercase"
            style={{ color: "rgba(255,255,255,0.45)" }}
          >
            Click anywhere or press Esc to close
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
